import SimpleContainer from '../../simpleComponents/SimpleContainer';
import SimpleInput from '../../simpleComponents/SimpleInput';
import React, { useState, useRef, useEffect } from 'react';
import HoverContainer from './HoverContainer';

import './SearchInput.scss';

const SearchInput = ({
    leftIcon,
    value,
    rightIcon,
    onSearch,
    tintColor,
    IconStyle,
    textStyle,
    queryResult,
    isPerforming,
    getButtonTextFunction,
    getSelectValueFunction,
    buttonPressFunction,
    emptyActionText,
    onEmptyAction,
    clearOnSelect,
    usePortal = true,
    title,
    error,
    labelAccessory,
    style: _style,
    className,
    ...props
}) => {

    const [query, setQuery] = useState(value);
    const [showResults, setShowResults] = useState(false);
    const [isOpening, setIsOpening] = useState(false);
    const targetRef = useRef(null);
    const inputRef = useRef(null);
    const blurTimerRef = useRef(null);

    useEffect(() => {
        return () => {
            if (blurTimerRef.current) clearTimeout(blurTimerRef.current);
        };
    }, []);

    const hasDropdown =
        queryResult !== undefined ||
        isPerforming !== undefined ||
        Boolean(getButtonTextFunction) ||
        Boolean(buttonPressFunction) ||
        (Boolean(emptyActionText) && Boolean(onEmptyAction));

    useEffect(() => {
        setQuery(String(value ?? ''));
    }, [value]);

    const handleInputChange = (event) => {
        setQuery(event.target.value);
        onSearch(event.target.value);
        if (hasDropdown) {
            setShowResults(true);
        }
    };

    const handleFocus = () => {
        if (blurTimerRef.current) {
            clearTimeout(blurTimerRef.current);
            blurTimerRef.current = null;
        }
        if (!hasDropdown) return;

        setShowResults(true);
        setIsOpening(true);

        const q = String(query ?? '');
        onSearch?.(q.trim() ? q : '');
    };

    const handleBlur = () => {
        blurTimerRef.current = setTimeout(() => {
            blurTimerRef.current = null;
            setShowResults(false);
            setIsOpening(false);
        }, 250);
    };

    useEffect(() => {
        if (!hasDropdown || !showResults || !isOpening) return;

        if (isPerforming) {
            setIsOpening(false);
            return;
        }
        if (Array.isArray(queryResult) && queryResult.length > 0) {
            setIsOpening(false);
            return;
        }

        const id = setTimeout(() => setIsOpening(false), 250);
        return () => clearTimeout(id);
    }, [hasDropdown, showResults, isOpening, isPerforming, queryResult]);

    function hoverButtonPressed(text, result) {
        if (blurTimerRef.current) {
            clearTimeout(blurTimerRef.current);
            blurTimerRef.current = null;
        }
        const cleanedText = String(text ?? '').trimEnd();
        const fieldValue = String(getSelectValueFunction?.(result) ?? cleanedText).trimEnd();
        setShowResults(false);
        buttonPressFunction?.(fieldValue, result);
        if (clearOnSelect) {
            setQuery('');
            onSearch?.('');
        } else {
            setQuery(fieldValue);
            onSearch?.(fieldValue);
        }
        // Close mobile keyboard; value already committed via onSearch above.
        requestAnimationFrame(() => {
            inputRef.current?.blur?.();
        });
    }

    return (
        <SimpleContainer className={['lw-searchInput', className].filter(Boolean).join(' ')}>
            <SimpleInput
                title={title}
                labelAccessory={labelAccessory}
                ref={targetRef}
                inputRef={inputRef}
                acceptExternalValueWhileFocused
                value={query}
                leftIcon={leftIcon}
                rightIcon={rightIcon}
                tintColor={tintColor}
                IconStyle={IconStyle}
                textStyle={textStyle}
                onChange={handleInputChange}
                onFocus={handleFocus}
                onBlur={handleBlur}
                error={error}
                timeToWaitInMilli={0}
                {...props}
            />
            {hasDropdown && showResults && (
                <HoverContainer
                    targetRef={targetRef}
                    className="lw-searchInput__hover"
                    usePortal={usePortal}
                    queryResult={queryResult}
                    isPerforming={Boolean(isPerforming) || isOpening}
                    getButtonTextFunction={getButtonTextFunction}
                    onPressButtonFunction={hoverButtonPressed}
                    query={query}
                    emptyActionText={emptyActionText}
                    onEmptyAction={onEmptyAction}
                    onClose={() => { setShowResults(false) }}

                />
            )}
        </SimpleContainer>
    );
};

export default SearchInput;
